<template>
	<div class="stepAppearance">
		<header class="stepAppearance__header">
			<h2 class="stepAppearance__title">{{ $t('setupWizard_appearance_title') }}</h2>
			<p class="stepAppearance__caption">{{ $t('setupWizard_appearance_caption') }}</p>
		</header>

		<section class="stepAppearance__section">
			<div class="stepAppearance__sectionLabel">{{ $t('settings_theme_group_theme') }}</div>
			<div
				class="stepAppearance__tileRow"
				role="radiogroup"
			>
				<button
					:aria-checked="store.themeMode === option.value"
					:class="[
						'stepAppearance__tile',
						{ 'stepAppearance__tile--active': store.themeMode === option.value },
					]"
					:key="option.value"
					@click="onThemeModeChange(option.value)"
					role="radio"
					type="button"
					v-for="option in themeModeOptions"
				>
					<i
						:class="iconClass(option.icon)"
						class="stepAppearance__tileIcon"
					/>
					<span>{{ option.label }}</span>
				</button>
			</div>
		</section>

		<section class="stepAppearance__section">
			<div class="stepAppearance__sectionLabel">{{ $t('settings_theme_primary_color') }}</div>
			<div class="stepAppearance__swatchRow">
				<button
					:aria-label="color.label"
					:class="[
						'stepAppearance__swatch',
						{ 'stepAppearance__swatch--active': color.name === store.themePrimaryColor },
					]"
					:key="color.name"
					:style="{ backgroundColor: primitives[color.name]?.['500'] ?? '#888' }"
					:title="color.label"
					@click="onPrimaryColorChange(color.name)"
					type="button"
					v-for="color in PRIMARY_COLORS"
				/>
				<button
					:aria-label="$t('settings_theme_custom_color_label')"
					:class="[
						'stepAppearance__swatch stepAppearance__swatch--custom',
						{ 'stepAppearance__swatch--active': store.themePrimaryColor === 'custom' },
					]"
					:style="
						store.themePrimaryColor === 'custom'
							? { backgroundColor: store.themePrimaryColorCustom }
							: undefined
					"
					:title="$t('settings_theme_custom_color_label')"
					@click="openPrimaryCustomPicker($event)"
					type="button"
				>
					<i
						:class="eyeDropperIconClass"
						v-if="store.themePrimaryColor !== 'custom'"
					/>
				</button>
			</div>
		</section>

		<section class="stepAppearance__section">
			<div class="stepAppearance__sectionLabel">{{ $t('settings_theme_surface') }}</div>
			<div class="stepAppearance__swatchRow">
				<button
					:aria-label="surface.label"
					:class="[
						'stepAppearance__swatch',
						{ 'stepAppearance__swatch--active': surface.name === store.themeSurfaceColor },
					]"
					:key="surface.name"
					:style="{ backgroundColor: primitives[surface.name]?.['500'] ?? '#888' }"
					:title="surface.label"
					@click="onSurfaceColorChange(surface.name)"
					type="button"
					v-for="surface in SURFACE_COLORS"
				/>
			</div>
		</section>

		<ColorPickerSheet
			:label="$t('settings_theme_primary_color')"
			:model-value="store.themePrimaryColorCustom"
			@commit="onPrimaryCustomColorCommit"
			@update:model-value="onPrimaryCustomColorChange"
			ref="primaryCustomPickerRef"
		/>
	</div>
</template>

<script lang="ts">
import { computed, defineComponent, inject, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { applyDarkMode } from 'src/composables/useDarkMode';
import { useUserInterfaceSettingsStore } from 'src/stores/userInterfaceSettings';
import {
	PRIMARY_COLORS,
	type PrimaryColorName,
	SURFACE_COLORS,
	type SurfaceColorName,
	getPresetPrimitives,
} from 'src/theme/themeConfig';

import ColorPickerSheet from 'src/components/common/ColorPickerSheet.vue';

import { CommandBus } from 'src/util/command/CommandBus';

type ColorPickerSheetInstance = {
	open: (event: Event) => void;
};

export default defineComponent({
	components: { ColorPickerSheet },
	name: 'StepAppearance',
	setup() {
		const { t } = useI18n();
		const store = useUserInterfaceSettingsStore();
		const commandBus = inject<CommandBus>('commandBus');

		const primitives = computed(() => getPresetPrimitives());

		const themeModeOptions = computed<Array<{ icon: string; label: string; value: 'auto' | 'dark' | 'light' }>>(
			() => [
				{ icon: 'sun', label: t('settings_theme_toggle_mode_light'), value: 'light' },
				{ icon: 'moon', label: t('settings_theme_toggle_mode_dark'), value: 'dark' },
				{ icon: 'circle-half-stroke', label: t('settings_theme_toggle_mode_automatic'), value: 'auto' },
			]
		);

		const eyeDropperIconClass = computed<string>(() =>
			store.menuItemsSolid ? 'fa-solid fa-eye-dropper' : 'fa-light fa-eye-dropper'
		);

		function iconClass(name: string): string {
			return store.menuItemsSolid ? `fa-solid fa-${name}` : `fa-light fa-${name}`;
		}

		function onThemeModeChange(mode: 'auto' | 'dark' | 'light'): void {
			store.setThemeMode(mode, commandBus);
			applyDarkMode(mode);
		}

		function onPrimaryColorChange(color: PrimaryColorName): void {
			store.setThemePrimaryColor(color, commandBus);
		}

		function onSurfaceColorChange(surface: SurfaceColorName): void {
			store.setThemeSurfaceColor(surface, commandBus);
		}

		function onPrimaryCustomColorChange(hex: string): void {
			store.setThemePrimaryColorCustom(hex);
		}

		function onPrimaryCustomColorCommit(hex: string): void {
			store.setThemePrimaryColorCustom(hex, commandBus);
		}

		const primaryCustomPickerRef = ref<ColorPickerSheetInstance | null>(null);

		function openPrimaryCustomPicker(event: Event): void {
			primaryCustomPickerRef.value?.open(event);
		}

		return {
			PRIMARY_COLORS,
			SURFACE_COLORS,
			eyeDropperIconClass,
			iconClass,
			onPrimaryColorChange,
			onPrimaryCustomColorChange,
			onPrimaryCustomColorCommit,
			onSurfaceColorChange,
			onThemeModeChange,
			openPrimaryCustomPicker,
			primaryCustomPickerRef,
			primitives,
			store,
			themeModeOptions,
		};
	},
});
</script>

<style lang="scss" scoped>
.stepAppearance {
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
}

.stepAppearance__header {
	margin-bottom: 0;
}

.stepAppearance__title {
	font-size: var(--text-xl);
	font-weight: 600;
	letter-spacing: -0.01em;
	margin: 0 0 0.125rem;
}

.stepAppearance__caption {
	color: var(--p-text-muted-color);
	font-size: var(--text-sm);
	margin: 0;
}

.stepAppearance__section {
	display: flex;
	flex-direction: column;
	gap: 0.625rem;
}

.stepAppearance__sectionLabel {
	color: var(--p-text-color);
	font-size: var(--text-xs-plus);
	font-weight: 600;
	letter-spacing: 0.02em;
	text-transform: uppercase;
}

.stepAppearance__tileRow {
	display: grid;
	gap: 0.75rem;
	grid-template-columns: repeat(3, minmax(0, 1fr));
}

.stepAppearance__tile {
	align-items: center;
	background: var(--p-surface-0);
	border: 2px solid var(--p-surface-200);
	border-radius: 0.5rem;
	color: var(--p-text-color);
	cursor: pointer;
	display: flex;
	flex-direction: column;
	font-size: var(--text-sm);
	gap: 0.5rem;
	padding: 1rem 0.75rem;
	transition:
		background-color 150ms ease,
		border-color 150ms ease;

	&:hover:not(.stepAppearance__tile--active) {
		background: var(--hover-light);
	}
}

.stepAppearance__tile--active {
	background: color-mix(in srgb, var(--p-primary-500) 8%, transparent);
	border-color: var(--p-primary-500);
}

.stepAppearance__tileIcon {
	font-size: var(--text-xl);
}

.stepAppearance__swatchRow {
	align-items: center;
	display: flex;
	flex-wrap: wrap;
	gap: 0.5rem;
}

.stepAppearance__swatch {
	background: var(--p-surface-100);
	border: 2px solid transparent;
	border-radius: 9999px;
	cursor: pointer;
	height: 2rem;
	padding: 0;
	transition:
		border-color 150ms ease,
		transform 150ms ease;
	width: 2rem;

	&:hover:not(.stepAppearance__swatch--active) {
		transform: scale(1.05);
	}
}

.stepAppearance__swatch--active {
	border-color: var(--p-surface-700);
	box-shadow:
		0 0 0 2px var(--p-surface-0),
		0 0 0 4px var(--p-primary-400);
}

.stepAppearance__swatch--custom {
	align-items: center;
	color: var(--p-text-muted-color);
	display: flex;
	font-size: var(--text-xs);
	justify-content: center;
}
</style>

<style lang="scss">
.body--dark .stepAppearance__tile {
	background: var(--p-surface-800);
	border-color: var(--p-surface-700);
}

.body--dark .stepAppearance__tile:hover:not(.stepAppearance__tile--active) {
	background: var(--hover-dark);
}

.body--dark .stepAppearance__tile--active {
	background: color-mix(in srgb, var(--p-primary-400) 12%, transparent);
	border-color: var(--p-primary-400);
}

.body--dark .stepAppearance__swatch--active {
	border-color: var(--p-surface-200);
	box-shadow:
		0 0 0 2px var(--p-surface-900),
		0 0 0 4px var(--p-primary-400);
}
</style>
